import { useAuth, useClerk } from "@clerk/tanstack-react-start";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import z from "zod";

import { validateRedirectPath } from "~/lib/shared/post-auth-routing";

export const Route = createFileRoute("/accept-invitation/")({
  // The redirect is followed with `location.replace`, so only a same-site
  // path may pass; anything else falls back to the default destination.
  validateSearch: z.object({
    token: z.string(),
    redirect: z
      .string()
      .optional()
      .transform((path) =>
        path ? (validateRedirectPath(path) ?? undefined) : undefined,
      ),
  }),
  component: AcceptInvitationPage,
});

function AcceptInvitationPage() {
  const [status, setStatus] = useState<"signing-out" | "redirecting">(
    "signing-out",
  );
  const search = useSearch({ from: "/accept-invitation/" });
  const token = search.token;
  const redirect = search.redirect ?? "/settings/profile";

  const { signOut } = useClerk();
  const { isLoaded, isSignedIn } = useAuth();

  useEffect(() => {
    if (!isLoaded) return;

    const encodedToken = encodeURIComponent(token);
    const encodedRedirect = encodeURIComponent(redirect);
    const acceptTokenUrl = `/accept-token?token=${encodedToken}&redirect=${encodedRedirect}`;

    if (isSignedIn) {
      setStatus("redirecting");
      void signOut({ redirectUrl: acceptTokenUrl });
    } else {
      window.location.replace(acceptTokenUrl);
    }
  }, [isLoaded, isSignedIn, signOut, token, redirect]);

  return (
    <div className="mx-auto flex min-h-screen w-full items-center justify-center">
      <div className="mx-auto max-w-md p-8">
        {status === "signing-out" ? "Preparing your account…" : "Redirecting…"}
      </div>
    </div>
  );
}
